/**
 * The listening-blade's two long moves live, timed to the rules' beats (packages/shared data/longMoves/moonSword.ts) instead
 * of their page clocks, the way fx/longBrocade.ts does jy-dragon:
 *  - ms-kiss (the lock routine): the invitation over the lock wind-up, the take, the two turns, the dip, the three
 *    touches, the spin-away cut, the bow (sampleArt/arts/moon.ts `kiss`, MOON_KISS_T).
 *  - ms-eclipse (ท่าใหญ่): the raised moon over the 0.8 s tell, the dim, three blinks, the disc, the bite, the crescent,
 *    the return (`eclipse`, MOON_ECLIPSE_T).
 * The afterimages come from the beats' `ai` (render/longMove.ts), so the samples' own are left out.
 */
import { longMoveOf, type LongMoveDef } from '@nine/shared';
import { longRetime } from '../longMove';
import { registerFx } from './registry';
import { portOf } from './sampleArt/port';
import { MOON_ECLIPSE_T as E, MOON_KISS_T as K } from './sampleArt/arts/moon';

const atPose = (def: LongMoveDef, pose: string): number => def.beats.find((b) => b.pose === pose)?.at ?? -1;

registerFx([], (host) => {
  const kiss = longMoveOf('ms-kiss');
  const kSpec = portOf('ms-kiss');
  if (kiss && kSpec) {
    const p = (pose: string): number => atPose(kiss, pose);
    const marks = [p('msTakeHand'), p('msTurnUnder'), p('msTurnFast'), p('msDip'), p('msTipTouch'), p('msSpinAway'), p('msBow'), p('msStance')];
    if (marks.every((x) => x >= 0)) {
      const anchors: [number, number][] = [
        [-0.35, 0.02],
        [marks[0]!, K.start],
        [marks[1]!, K.turn[0]],
        [marks[2]!, K.turn[1]],
        [marks[3]!, K.dip],
        [marks[4]!, K.touch],
        [marks[5]!, K.spin],
        [marks[6]!, K.bow],
        [marks[7]!, K.stance],
        [kiss.len, K.end],
      ];
      kSpec.ghosts = undefined;
      kSpec.retime = longRetime(host.world, 'ms-kiss', anchors, K.end);
    }
  }
  const ecl = longMoveOf('ms-eclipse');
  const eSpec = portOf('ms-eclipse');
  if (ecl && eSpec) {
    const p = (pose: string): number => atPose(ecl, pose);
    const marks = [p('msDim'), p('msBlinkA'), p('msBlinkB'), p('msBlinkC'), p('msDiscRaise'), p('msDiscBite'), p('msCrescent'), p('msSheathe'), p('msStance')];
    if (marks.every((x) => x >= 0)) {
      const anchors: [number, number][] = [
        [-ecl.opener.tell, 0.05],
        [marks[0]!, E.start],
        [marks[1]!, E.blink[0]],
        [marks[2]!, E.blink[1]],
        [marks[3]!, E.blink[2]],
        [marks[4]!, E.disc],
        [marks[5]!, E.bite],
        [marks[6]!, E.cres],
        [marks[7]!, E.ret],
        [marks[8]!, E.fade],
        [ecl.len, E.end],
      ];
      eSpec.ghosts = undefined;
      eSpec.retime = longRetime(host.world, 'ms-eclipse', anchors, E.end);
    }
  }
  return {};
});
